import {
  type Counterparty,
  CURRENCIES,
  can,
  type Direction,
  PRECONTRACT_STAGES,
  PROJECT_STAGES,
  type ProjectDetail,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { counterpartiesQuery, directionsQuery, projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';

/**
 * Мастер «Новый проект» (ПФ-09): шесть шагов, черновик сохраняется на этом устройстве
 * после каждого шага. Шаблон — копия структуры существующего проекта без фактических данных (ПФ-10).
 */

const STEPS = [
  { title: 'Основное', hint: 'компания, направление, заказчик, договор, регион, валюта, даты' },
  { title: 'Шаблон', hint: 'пустой проект или структура существующего' },
  { title: 'Структура', hint: 'подпроекты и объекты, шифры ПСД' },
  { title: 'Команда', hint: 'роли и люди, лимиты согласования' },
  { title: 'Справочники', hint: 'нумерация, склады, ставки, маршруты' },
  { title: 'Проверка', hint: 'что будет создано' },
];

interface Draft {
  step: number;
  legalEntityId: string;
  directionId: string;
  newDirection: string;
  siteName: string;
  code: string;
  name: string;
  customerId: string;
  newCustomer: string;
  contractNumber: string;
  contractDate: string;
  region: string;
  address: string;
  currency: (typeof CURRENCIES)[number];
  stage: keyof typeof PROJECT_STAGES;
  startDate: string;
  finishDate: string;
  templateProjectId: string;
  objects: Array<{ code: string; name: string }>;
  subprojects: Array<{ code: string; name: string }>;
}

const KEY = 'epc:project-wizard';

const emptyDraft = (le: string): Draft => ({
  step: 0,
  legalEntityId: le,
  directionId: '',
  newDirection: '',
  siteName: '',
  code: '',
  name: '',
  customerId: '',
  newCustomer: '',
  contractNumber: '',
  contractDate: '',
  region: '',
  address: '',
  currency: 'KZT',
  stage: 'preparation',
  startDate: '',
  finishDate: '',
  templateProjectId: '',
  objects: [],
  subprojects: [],
});

function loadDraft(le: string): Draft {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...emptyDraft(le), ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    /* черновика нет */
  }
  return emptyDraft(le);
}

function saveDraft(d: Draft | null): void {
  try {
    if (d) localStorage.setItem(KEY, JSON.stringify(d));
    else localStorage.removeItem(KEY);
  } catch {
    /* хранилище недоступно — черновик не сохранится */
  }
}

export function ProjectWizard({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [d, setD] = useState<Draft>(() => loadDraft(me.legalEntities[0]?.id ?? ''));
  // ПФ-23: стадию позже тендера при создании ставит Директор (право утверждать бюджет по юрлицу)
  const lateStageAllowed = can(me.access, 'budget.approve', { legalEntityId: d.legalEntityId });
  const dirs = useQuery({ ...directionsQuery, enabled: open });
  const customers = useQuery({ ...counterpartiesQuery('customer'), enabled: open });
  const all = useQuery({ ...projectsQuery('all'), enabled: open });

  useEffect(() => {
    if (open) saveDraft(d);
  }, [d, open]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const leDirs = (dirs.data ?? []).filter(
    (x: Direction) => x.legalEntityId === d.legalEntityId && x.isActive,
  );
  const template = (all.data ?? []).find((p) => p.id === d.templateProjectId);

  const create = useMutation({
    mutationFn: async () => {
      let directionId = d.directionId || null;
      if (!directionId && d.newDirection.trim()) {
        const dir = await api<Direction>('/directions', {
          body: { legalEntityId: d.legalEntityId, name: d.newDirection.trim() },
        });
        directionId = dir.id;
      }
      let customerId = d.customerId || null;
      if (!customerId && d.newCustomer.trim()) {
        const c = await api<Counterparty>('/counterparties', {
          body: { name: d.newCustomer.trim(), isCustomer: true },
        });
        customerId = c.id;
      }
      const base = {
        legalEntityId: d.legalEntityId,
        directionId,
        siteName: d.siteName || null,
        contractNumber: d.contractNumber || null,
        contractDate: d.contractDate || null,
        customerId,
        region: d.region || null,
        address: d.address || null,
        currency: d.currency,
        stage: d.stage,
      };
      const p = await api<ProjectDetail>('/projects', {
        body: {
          ...base,
          code: d.code,
          name: d.name,
          startDate: d.startDate || null,
          finishDate: d.finishDate || null,
        },
      });
      for (const [i, o] of d.objects.entries()) {
        if (o.name.trim().length < 2) continue;
        await api(`/projects/${p.id}/objects`, {
          body: { code: o.code || null, name: o.name, sortOrder: i + 1 },
        });
      }
      for (const s of d.subprojects) {
        if (s.name.trim().length < 2 || !s.code) continue;
        await api('/projects', { body: { ...base, parentId: p.id, code: s.code, name: s.name } });
      }
      return p;
    },
    onSuccess: (p) => {
      saveDraft(null);
      setD(emptyDraft(me.legalEntities[0]?.id ?? ''));
      for (const k of ['portfolio', 'projects', 'directions', 'counterparties'])
        qc.invalidateQueries({ queryKey: [k] });
      toast('Проект создан');
      onOpenChange(false);
      navigate({ to: `/projects/${p.id}` });
    },
  });

  const step1Ok = d.legalEntityId && d.code.trim() && d.name.trim().length >= 2;
  const next = () => setD((x) => ({ ...x, step: Math.min(5, x.step + 1) }));
  const back = () => setD((x) => ({ ...x, step: Math.max(0, x.step - 1) }));

  const applyTemplate = (id: string) => {
    const src = (all.data ?? []).find((p) => p.id === id);
    setD((x) => ({
      ...x,
      templateProjectId: id,
      objects: src ? src.objects.map((o) => ({ code: o.code ?? '', name: o.name })) : x.objects,
      stage: src ? 'preparation' : x.stage,
    }));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      title="Новый проект"
      description={`шаг ${d.step + 1} из 6 · ${STEPS[d.step]!.title.toLowerCase()}: ${STEPS[d.step]!.hint}`}
      footer={
        <>
          <span className="mr-auto text-[12px] text-ink-2">Черновик сохраняется на этом устройстве</span>
          {d.step > 0 ? (
            <Button variant="ghost" onClick={back}>
              Назад
            </Button>
          ) : null}
          {d.step < 5 ? (
            <Button variant="primary" disabled={!step1Ok} onClick={next}>
              Далее
            </Button>
          ) : (
            <Button
              variant="accent"
              loading={create.isPending}
              disabled={!step1Ok}
              onClick={() => create.mutate()}
            >
              Создать проект
            </Button>
          )}
        </>
      }
    >
      <ol className="mb-5 flex flex-wrap gap-1.5">
        {STEPS.map((s, i) => (
          <li key={s.title}>
            <button
              type="button"
              disabled={i > 0 && !step1Ok}
              onClick={() => set('step', i)}
              className={clsx(
                'inline-flex h-7 items-center gap-1.5 rounded-sm px-2 text-[12px] font-medium',
                i === d.step ? 'bg-ink text-on-ink' : i < d.step ? 'bg-tile text-ink' : 'text-ink-2',
              )}
            >
              <span className="num">{i + 1}</span> {s.title}
            </button>
          </li>
        ))}
      </ol>

      {d.step === 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Компания">
            <Select
              value={d.legalEntityId}
              onChange={(e) => setD({ ...d, legalEntityId: e.target.value, directionId: '' })}
            >
              {me.legalEntities.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Направление" hint={d.directionId ? undefined : 'или впишите новое ниже'}>
            <Select value={d.directionId} onChange={(e) => set('directionId', e.target.value)}>
              <option value="">— новое или без направления —</option>
              {leDirs.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
          </Field>
          {!d.directionId ? (
            <Field label="Новое направление" className="sm:col-span-2">
              <Input
                value={d.newDirection}
                placeholder="например, Водоподготовка и очистные"
                onChange={(e) => set('newDirection', e.target.value)}
              />
            </Field>
          ) : null}
          <Field label="Код проекта">
            <Input value={d.code} placeholder="227-23-ВВ3" onChange={(e) => set('code', e.target.value)} />
          </Field>
          <Field label="Название">
            <Input value={d.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label="Площадка или программа" hint="Объединяет проекты в названии: «Карабатан · …»">
            <Input value={d.siteName} onChange={(e) => set('siteName', e.target.value)} />
          </Field>
          <Field
            label="Стадия"
            hint={
              lateStageAllowed
                ? undefined
                : 'Без подписанного доходного договора проект начинается с подготовки или тендера; другую стадию ставит Директор (ПФ-23)'
            }
          >
            <Select value={d.stage} onChange={(e) => set('stage', e.target.value as Draft['stage'])}>
              {Object.entries(PROJECT_STAGES).map(([k, v]) => (
                <option
                  key={k}
                  value={k}
                  disabled={!lateStageAllowed && !(PRECONTRACT_STAGES as readonly string[]).includes(k)}
                >
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Заказчик">
            <Select value={d.customerId} onChange={(e) => set('customerId', e.target.value)}>
              <option value="">— новый —</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.shortName ?? c.name}
                </option>
              ))}
            </Select>
          </Field>
          {!d.customerId ? (
            <Field label="Новый заказчик">
              <Input value={d.newCustomer} onChange={(e) => set('newCustomer', e.target.value)} />
            </Field>
          ) : (
            <div />
          )}
          <Field label="Договор №">
            <Input value={d.contractNumber} onChange={(e) => set('contractNumber', e.target.value)} />
          </Field>
          <Field label="Дата договора">
            <Input type="date" value={d.contractDate} onChange={(e) => set('contractDate', e.target.value)} />
          </Field>
          <Field label="Регион">
            <Input
              value={d.region}
              placeholder="Атырауская область"
              onChange={(e) => set('region', e.target.value)}
            />
          </Field>
          <Field label="Валюта договора">
            <Select value={d.currency} onChange={(e) => set('currency', e.target.value as Draft['currency'])}>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Начало">
            <Input type="date" value={d.startDate} onChange={(e) => set('startDate', e.target.value)} />
          </Field>
          <Field label="Завершение">
            <Input type="date" value={d.finishDate} onChange={(e) => set('finishDate', e.target.value)} />
          </Field>
        </div>
      ) : null}

      {d.step === 1 ? (
        <div className="flex flex-col gap-4">
          <p className="text-[14px] text-ink-2">
            Шаблоны по типу объекта (водовод, резервуары, очистные, ТО ГТУ) соберём из закрытых проектов.
            Сейчас можно взять структуру объектов любого проекта — без сумм, оплат и документов.
          </p>
          <Field label="Основа">
            <Select value={d.templateProjectId} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">Пустой проект</option>
              {(all.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  Структура проекта {p.code} {p.name}
                </option>
              ))}
            </Select>
          </Field>
          {template ? (
            <p className="text-[13px] text-ink-2">Будет скопировано объектов: {template.objects.length}.</p>
          ) : null}
        </div>
      ) : null}

      {d.step === 2 ? (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-2 text-[14px] font-semibold text-ink">Объекты</h3>
            <ul className="flex flex-col gap-2">
              {d.objects.map((o, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: строки черновика без собственного id
                <li key={`o${i}`} className="flex gap-2">
                  <Input
                    aria-label="Шифр"
                    className="w-28"
                    placeholder="ВНС-1"
                    value={o.code}
                    onChange={(e) =>
                      set(
                        'objects',
                        d.objects.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)),
                      )
                    }
                  />
                  <Input
                    aria-label="Название"
                    placeholder="Насосная станция"
                    value={o.name}
                    onChange={(e) =>
                      set(
                        'objects',
                        d.objects.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                      )
                    }
                  />
                  <IconButton
                    label="Убрать объект"
                    onClick={() =>
                      set(
                        'objects',
                        d.objects.filter((_, j) => j !== i),
                      )
                    }
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                </li>
              ))}
            </ul>
            <Button
              className="mt-2"
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() => set('objects', [...d.objects, { code: '', name: '' }])}
            >
              Объект
            </Button>
          </div>
          <div>
            <h3 className="mb-2 text-[14px] font-semibold text-ink">Подпроекты</h3>
            <p className="mb-2 text-[13px] text-ink-2">
              Свои бюджет, график, команда и доступы; свод поднимается в проект.
            </p>
            <ul className="flex flex-col gap-2">
              {d.subprojects.map((s, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: строки черновика без собственного id
                <li key={`s${i}`} className="flex gap-2">
                  <Input
                    aria-label="Код"
                    className="w-36"
                    value={s.code}
                    onChange={(e) =>
                      set(
                        'subprojects',
                        d.subprojects.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)),
                      )
                    }
                  />
                  <Input
                    aria-label="Название"
                    value={s.name}
                    onChange={(e) =>
                      set(
                        'subprojects',
                        d.subprojects.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                      )
                    }
                  />
                  <IconButton
                    label="Убрать подпроект"
                    onClick={() =>
                      set(
                        'subprojects',
                        d.subprojects.filter((_, j) => j !== i),
                      )
                    }
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                </li>
              ))}
            </ul>
            <Button
              className="mt-2"
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() =>
                set('subprojects', [
                  ...d.subprojects,
                  { code: `${d.code}-${d.subprojects.length + 1}`, name: '' },
                ])
              }
            >
              Подпроект
            </Button>
          </div>
        </div>
      ) : null}

      {d.step === 3 ? (
        <div className="flex flex-col gap-3 text-[14px] text-ink-2">
          <p>
            Команду назначают после создания: «Администрирование → Пользователи» — роль на этот проект
            (руководитель проекта, ГИП, снабженец, прораб). Доступ к проекту открывает его подпроекты, к
            подпроекту — только его.
          </p>
          <p>Деньги выдаются людям, а не ролям: уровень доступа к суммам назначает владелец компании.</p>
          <Link
            to="/admin/members"
            className="font-medium text-accent-text underline-offset-4 hover:underline"
          >
            Открыть пользователей
          </Link>
        </div>
      ) : null}

      {d.step === 4 ? (
        <p className="text-[14px] text-ink-2">
          Нумерация документов, склады, ставки и маршруты согласования берутся из настроек компании. Свои для
          проекта появятся вместе с модулями закупок (этап 2), договоров (этап 3) и склада.
        </p>
      ) : null}

      {d.step === 5 ? (
        <dl className="grid grid-cols-[minmax(140px,max-content)_1fr] gap-x-6 gap-y-2 text-[14px]">
          <dt className="text-ink-2">Компания</dt>
          <dd>{me.legalEntities.find((x) => x.id === d.legalEntityId)?.name}</dd>
          <dt className="text-ink-2">Направление</dt>
          <dd>{leDirs.find((x) => x.id === d.directionId)?.name ?? (d.newDirection || '—')}</dd>
          <dt className="text-ink-2">Проект</dt>
          <dd>
            {d.code} {d.siteName ? `${d.siteName} · ` : ''}
            {d.name}
          </dd>
          <dt className="text-ink-2">Заказчик, договор</dt>
          <dd>
            {(customers.data ?? []).find((c) => c.id === d.customerId)?.name ?? (d.newCustomer || '—')}
            {d.contractNumber ? `, № ${d.contractNumber}` : ''}
          </dd>
          <dt className="text-ink-2">Стадия, валюта</dt>
          <dd>
            {PROJECT_STAGES[d.stage]}, {d.currency}
          </dd>
          <dt className="text-ink-2">Объекты</dt>
          <dd>
            {d.objects
              .filter((o) => o.name)
              .map((o) => o.code || o.name)
              .join(', ') || '—'}
          </dd>
          <dt className="text-ink-2">Подпроекты</dt>
          <dd>
            {d.subprojects
              .filter((s) => s.name)
              .map((s) => s.name)
              .join(', ') || '—'}
          </dd>
        </dl>
      ) : null}
      <ErrorBox error={create.error} className="mt-4" />
    </Dialog>
  );
}
